// src/workspace/client/components/WorkspaceSidebar.tsx
import { CreateChannelDialog } from "../../workspace/components/create-channel-dialogue";
import { useState, useRef, useEffect, useMemo } from "react";
import type {
	Channel,
	ChannelWithChildren,
	FavouriteChannels,
	Workspace,
} from "@/utils/custom-types";
import { useTabs } from "@/contexts/tabs-context";
import { useSplitPanel } from "@/contexts/split-panel-context";
import { Button } from "@/components/ui/button";
import {
	Plus,
	ChevronRight,
	ChevronDown,
	FileText,
	FolderOpen,
	Folder,
	MessageSquare,
	MessagesSquare,
	Video,
	HardDrive,
	FolderKanban,
	Zap,
	Lock,
	Trello,
	LayoutGrid,
	Home,
	FileSpreadsheet,
	X,
} from "lucide-react";
import { getActiveSection } from "./Sidebar";
import { useLocation, useNavigate, useRouter } from "@tanstack/react-router";
import { useWorkspaceSection } from "@/contexts/workspace-section-context";
import { DragOverlay, MouseSensor, useDndMonitor, useDraggable, useDroppable, useSensor, type DragEndEvent, type DragMoveEvent, type DragOverEvent, type DragStartEvent } from "@dnd-kit/core";
import { useDndContext } from "@/contexts/dnd-context";
import { snapCenterToCursor } from '@dnd-kit/modifiers';
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, trpc } from "@/utils/trpc";
import { useWorkspaceContext } from "@/contexts/workspace-context";
import { WorkspaceSidebarLoading } from "./loading-workspace-sidebar";
import { workspace } from "../../../../server/src/workspace/workspace-schema";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

interface InsertionHint {
	overId: number;
	edge?: 'before' | 'after';
	section: "public" | "private" | "favourites"
}

interface DropResult {
	fromChannel: Channel;
	toChannel: Channel;
	edge?: 'before' | 'after';
	section: "public" | "private" | "favourites"
	fromLevel: number;
	toLevel: number;
}

// Helper function to get appropriate icon for channel type
const getChannelIcon = (channel: ChannelWithChildren, isExpanded = true) => {
	const hasChildren = channel.children.length > 0;
	const iconSize = 16;

	switch (channel.type) {
		case "doc":
			if (hasChildren) {
				return isExpanded ? (
					<FolderOpen size={iconSize} />
				) : (
					<Folder size={iconSize} />
				);
			}
			return <FileText size={iconSize} />;
		case "message-group":
			if (hasChildren) {
				return <MessagesSquare size={iconSize} />;
			}
			return <MessageSquare size={iconSize} />;
		case "board":
			if (hasChildren) {
				return <LayoutGrid size={iconSize} />;
			}
			return <Trello size={iconSize} />;
		case "meeting":
			return <Video size={iconSize} />;
		case "storage":
			return <HardDrive size={iconSize} />;
		case "project":
			return <FolderKanban size={iconSize} />;
		case "automation":
			return <Zap size={iconSize} />;
		case "password":
			return <Lock size={iconSize} />;
		case "self-dm":
		case "dm":
			return <MessageSquare size={iconSize} />;
		default:
			return <FileText size={iconSize} />;
	}
};

// Helper function to get type-specific styling
const getChannelStyling = (
	channel: ChannelWithChildren,
	level: number,
	isActive = false,
	isOver = false,
) => {
	const overFill = isOver ? "bg-accent text-accent-foreground" : "";

	const baseClasses =
		"w-full justify-start text-left flex items-center gap-2 transition-all duration-200";
	const basePadding = 8 + level * 20;
	const extraChildIndent = level > 0 ? 8 : 0;
	const levelPadding = `${basePadding + extraChildIndent}px`;

	// Active state styling - takes precedence over other styles
	if (isActive) {
		return {
			className: `${baseClasses} bg-primary/10 text-primary border-primary/20 font-medium rounded-md hover:bg-primary/20`,
			style: { paddingLeft: levelPadding },
		};
	}

	// Level-specific styling using theme variables
	const levelStyles = {
		0: "font-semibold text-foreground bg-muted/30",
		1: "font-medium text-foreground",
		2: "font-normal text-muted-foreground text-sm",
		3: "font-normal text-muted-foreground text-sm opacity-75",
	};

	const typeColor = "hover:bg-accent hover:text-accent-foreground";
	const levelStyle =
		levelStyles[Math.min(level, 3) as keyof typeof levelStyles] ||
		levelStyles[3];

	return {
		className: `${baseClasses} ${typeColor} ${levelStyle} ${overFill} border border-transparent rounded-md`,
		style: { paddingLeft: levelPadding },
	};
};

// Channel tree item component
const ChannelTreeItem = ({
	channel,
	level = 0,
	onChannelClick,
	onChannelDoubleClick,
	onChannelMiddleClick,
	activeChannelId,
	workspaceId,
	dragOverlay = false,
	section,
	isChannelExpanded,
	toggleExpanded,
	onContextMenu,
	editingChannel,
	cancelEditingChannelName,
	saveChannelName,
}: {
	channel: ChannelWithChildren;
	level?: number;
	onChannelClick?: (channel: Channel) => void;
	onChannelDoubleClick?: (channel: Channel) => void;
	onChannelMiddleClick?: (e: React.MouseEvent, channel: Channel) => void;
	activeChannelId?: string | null;
	workspaceId: string;
	dragOverlay?: boolean;
	section: "public" | "private" | "favourites";
	isChannelExpanded: (id: string) => boolean;
	toggleExpanded: (id: string) => void;
	onContextMenu?: (
		e: React.MouseEvent,
		channel: Channel,
		section: "public" | "private" | "favourites",
	) => void;
	editingChannel: { channelID: number, section: "public" | "private" | "favourites" } | null;
	cancelEditingChannelName: () => void;
	saveChannelName: (name: string, channelId: number) => void;
}) => {
	const [tempChannelName, setTempChannelName] = useState(channel.title ?? "");
	const [isHovered, setIsHovered] = useState(false);

	let isExpanded;
	if (level === 0 && section === "favourites") {
		isExpanded = isChannelExpanded(`fav-root-${String(channel.id)}`);
	} else if (level > 0 && section === "favourites") {
		isExpanded = isChannelExpanded(`fav-${String(channel.id)}`);
	} else {
		isExpanded = isChannelExpanded(String(channel.id));
	}

	const hasChildren = channel.children.length > 0;
	const isActive = activeChannelId === channel.id.toString();
	const isChatChannel = ["message-group", "self-dm", "dm"].includes(channel.type);
	const unreadCount = channel.unreadCount ?? 0;
	const showUnreadBadge = isChatChannel && unreadCount > 0;

	// Handle toggle with debouncing
	const handleToggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		e.preventDefault();

		if (level === 0 && section === "favourites") {
			toggleExpanded(`fav-root-${String(channel.id)}`);
		} else if (level > 0 && section === "favourites") {
			toggleExpanded(`fav-${String(channel.id)}`);
		} else {
			toggleExpanded(String(channel.id));
		}
	};

	const handleNameKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
		e,
	) => {
		if (e.key === "Enter") {
			e.preventDefault();
			if (tempChannelName.length < 1) {
				// show an actual error
				console.error("channel name must be more than 1 character(s)")
			}
			saveChannelName(tempChannelName, channel.id);
		}
		if (e.key === "Escape") {
			e.preventDefault();
			setTempChannelName(channel.title ?? "")
			cancelEditingChannelName();
		}
	};

	let dndId;
	if (section === "favourites") {
		dndId = `${section}-${level}-channel-draggable-${channel.id}`
	} else {
		dndId = section
		? `${section}-channel-draggable-${channel.id}`
		: `channel-draggable-${channel.id}`;
	}

	let dndIdDrop;
	if (section === "favourites") {
		dndIdDrop = `${section}-${level}-channel-droppable-${channel.id}`
	} else {
		dndIdDrop = section
		? `${section}-channel-droppable-${channel.id}`
		: `channel-droppable-${channel.id}`;
	}

	const isEditingChannel = editingChannel?.channelID === channel.id && editingChannel.section === section

	const { attributes, listeners, setNodeRef: setDraggableNodeRef } = useDraggable({
		id: dndId,
		data: { channel, section, level },
		disabled: isEditingChannel
	});

	const {isOver, setNodeRef: setDroppableNodeRef} = useDroppable({
		id: dndIdDrop,
		data: { channel, isParent: hasChildren, isExpanded, section, level },
		disabled: isEditingChannel
	});

	const styling = getChannelStyling(channel, level, isActive, isOver);

	return (
		<div
			className={`relative ${dragOverlay ? "opacity-40" : ""}`}
		>
			{/* Connection line for nested items */}
			{level > 0 && (
				<div
					className="absolute border-border/50 border-l"
					style={{
						left: `${8 + (level - 1) * 20 + 8}px`,
						top: 0,
						bottom: hasChildren && isExpanded ? "50%" : "100%",
						width: "1px",
					}}
				/>
			)}
			{level > 0 && (
				<div
					className="absolute border-border/50 border-t"
					style={{
						left: `${8 + (level - 1) * 20 + 8}px`,
						top: "50%",
						width: "8px",
						height: "1px",
					}}
				/>
			)}

			<div className="flex items-center relative" data-row-id={`${channel.id}-${section}`}>
				<Button
					variant="ghost"
					className={`${styling.className} flex-1 relative cursor-pointer select-none`}	
					style={styling.style}
					onClick={(e) => {
						e.stopPropagation();
						if (isEditingChannel) return;
						onChannelClick?.(channel);
					}}
					onDoubleClick={(e) => {
						e.stopPropagation();
						if (isEditingChannel) return;
						onChannelDoubleClick?.(channel);
					}}
					onMouseDown={(e) => {
						if (e.button === 1) {
							e.stopPropagation();
						}
						if (isEditingChannel) return;
						onChannelMiddleClick?.(e, channel);
					}}
					onContextMenu={(e) => {
						e.preventDefault();
						e.stopPropagation();
						if (isEditingChannel) return;
						onContextMenu?.(e, channel, section);
					}}
					onMouseEnter={() => setIsHovered(true)}
					onMouseLeave={() => setIsHovered(false)}
					ref={node => {
						setDraggableNodeRef(node);
						setDroppableNodeRef?.(node);
					}}
					{...listeners}
					{...attributes}
				>
					<div className="flex min-w-0 flex-1 items-center gap-2">
						<span
							className="flex h-5 w-5 items-center justify-center text-muted-foreground hover:text-accent-foreground"
							onMouseDown={(e) => {
								e.stopPropagation();
								e.preventDefault();
							}}
							onClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								handleToggle(e); 
							}}
							onDoubleClick={(e) => {
								e.stopPropagation();
								e.preventDefault();
								handleToggle(e);
							}}
						>
							{isHovered && !dragOverlay ? (
								isExpanded ? (
									<ChevronDown size={12} />
								) : (
									<ChevronRight size={12} />
								)
							) : (
								getChannelIcon(channel, isExpanded)
							)}
						</span>

						{isEditingChannel ? (
							<Input
								autoFocus
								id="name"
								autoComplete="off"
								value={tempChannelName}
								onChange={(e) => {
									setTempChannelName(e.target.value)
								}}
								onKeyDown={handleNameKeyDown}
								onBlur={() => {
									setTempChannelName(channel.title ?? "")
									cancelEditingChannelName()
								}}
								placeholder="Enter channel name"
							/>
						) : (
							<span className="truncate">{channel.title}</span>
						)} 
					</div>

					{showUnreadBadge && (
						<span className="ml-2 inline-flex min-w-[24px] justify-center rounded-full bg-red-400 px-2 py-0.5 text-xs font-semibold text-secondary-foreground">
							{unreadCount > 99 ? "99+" : unreadCount}
						</span>
					)}
				</Button>
			</div>

			{isExpanded && (
				<div className="relative">
					{hasChildren ? (
						channel.children.map((child) => (
							<ChannelTreeItem
								key={child.id}
								channel={child}
								level={level + 1}
								onChannelClick={onChannelClick}
								onChannelDoubleClick={onChannelDoubleClick}
								onChannelMiddleClick={onChannelMiddleClick}
								activeChannelId={activeChannelId}
								workspaceId={workspaceId}
								section={section}
								isChannelExpanded={isChannelExpanded}
								toggleExpanded={toggleExpanded}
								onContextMenu={onContextMenu}
								editingChannel={editingChannel}
								cancelEditingChannelName={cancelEditingChannelName}
								saveChannelName={saveChannelName}
							/>
						))
					) : (
						<div 
							className={`py-2 h-8 text-xs text-muted-foreground italic`}
							style={{
								paddingLeft: `${parseInt(styling.style.paddingLeft, 10) + 32}px`,
							}}
						>
							No channels inside
						</div>
					)}
				</div>
			)}
		</div>
	);
};

const WorkspaceSidebar = ({
	currentWorkspace,
}: {
	currentWorkspace: Workspace;
}) => {
	const {
		channels,
		sidebarState,
		favouriteChannels,
		privateChannelOrders,
		sidebarStateLoading,
	} = useWorkspaceContext();

	const sidebarStateQueryKey = trpc.me.getSidebarState.queryKey({
		workspaceId: Number(currentWorkspace.id),
	});

	const channelsQueryKey = trpc.channel.getByUserAndWorkspace.queryKey({
		workspaceId: Number(currentWorkspace.id),
	});

	const updateChannelNameMutation = useMutation(
		trpc.channel.updateChannelName.mutationOptions({
			onError: (error) => {
				if (error.data?.code === "FORBIDDEN" || error.data?.httpStatus === 403) {
					toast.error("Unsufficient permissions to update the channel name.");
				} else {
					console.error("update channel name mutation failed:", error);
				}
			}
		}),
	);

	const deleteChannelMutation = useMutation(
		trpc.channel.delete.mutationOptions({
			onError: (error) => {
				if (error.data?.code === "FORBIDDEN" || error.data?.httpStatus === 403) {
					toast.error("Unsufficient permissions to update the channel name.");
				} else {
					console.error("update channel name mutation failed:", error);
				}
			}
		}),
	);

	const updateSidebarStateMutation = useMutation(
		trpc.me.updateSidebarState.mutationOptions({
			onSuccess: () => {
				// queryClient.invalidateQueries({ queryKey: sidebarStateQueryKey });
			},
		}),
	);

	const upsertFavouriteChannelMutation = useMutation(
		trpc.me.upsertFavouriteChannel.mutationOptions({
			onSuccess: () => {
				// queryClient.invalidateQueries({ queryKey: sidebarStateQueryKey });
			},
		}),
	);

	const updateFavouriteChannelMutation = useMutation(
		trpc.me.updateFavouriteChannel.mutationOptions({
			onSuccess: () => {
				// queryClient.invalidateQueries({ queryKey: sidebarStateQueryKey });
			},
		}),
	);

	const removeFavouriteChannelMutation = useMutation(
		trpc.me.removeFavouriteChannel.mutationOptions({
			onSuccess: () => {
				// queryClient.invalidateQueries({ queryKey: sidebarStateQueryKey });
			},
		}),
	);

	const updatePrivateChannelOrdersMutation = useMutation(
		trpc.me.updatePrivateChannelOrders.mutationOptions({
			onSuccess: () => {
				// queryClient.invalidateQueries({ queryKey: sidebarStateQueryKey });
			},
		}),
	);

	const reorderChannelsMutation = useMutation(
		trpc.channel.reorderChannels.mutationOptions({
			onSuccess: () => {
				queryClient.invalidateQueries({ queryKey: channelsQueryKey });
			},
			onError: (error) => {
				console.error("Reorder channels mutation failed:", error);
				queryClient.invalidateQueries({ queryKey: channelsQueryKey });
			},
		}),
	);

	const [editingChannel, setEditingChannel] = useState<{
		channelID: number,
		section: "public" | "private" | "favourites";
	} | null>(null);

	const [contextMenu, setContextMenu] = useState<{
		x: number;
		y: number;
		channel: Channel;
		section: "public" | "private" | "favourites";
		channels: Channel[];
		isFavourite: boolean;
	} | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);
	const [isDialogOpen, setIsDialogOpen] = useState(false);
	const [createChannelVisibility, setCreateChannelVisibility] = useState<"public" | "private">("public");
	const { pathname } = useLocation();

	type DeleteChannelPopupValue = { channel: Channel }
	const [deleteChannelPopup, setDeleteChannelPopup] = useState<DeleteChannelPopupValue | null>(null);

	const {
		openTemporaryTab,
		openPermanentTab,
		openTabInBackground,
		temporaryTab,
		createTabDataFromChannel,
		getTabByPath,
		confirmTab,
		tabs,
		updateTabNameForChannel
	} = useTabs();
	const { panels, focusedPanelId } = useSplitPanel();
	const defaultPanelId = focusedPanelId || panels[0]?.id || "default";
	const { activeSection } = useWorkspaceContext();

	const [dragMeta, setDragMeta] = useState<{
		channel: Channel;
		section: "public" | "private" | "favourites";
		level: number;
	} | null>(null);
	  
	const [insertionHint, setInsertionHint] = useState<InsertionHint | null>(null);
	const [dropResult, setDropResult] = useState<DropResult | null>(null);

	const containerRef = useRef<HTMLDivElement | null>(null);
	const [insertionTop, setInsertionTop] = useState<number | null>(null);

    const pointerStartYRef = useRef<number | null>(null);

	const pendingSavesRef = useRef<Map<string, boolean>>(new Map());

	const flushTimerRef = useRef<number | null>(null);

	const DEBOUNCE_MS = 2000;

	const handleChannelContextMenu = (
		e: React.MouseEvent,
		channel: Channel,
		section: "public" | "private" | "favourites",
	) => {
		e.preventDefault();

		// Approximate menu size (4 items) – tweak if you change the menu
		const MENU_WIDTH = 180;
		const MENU_HEIGHT = 150;
		const PADDING = 8; // padding from viewport edges

		let x = e.clientX;
		let y = e.clientY;

		const vw = window.innerWidth;
		const vh = window.innerHeight;

		// Clamp so the menu never goes off the right edge
		if (x + MENU_WIDTH + PADDING > vw) {
			x = vw - MENU_WIDTH - PADDING;
		}

		// Clamp so the menu never goes off the bottom edge
		if (y + MENU_HEIGHT + PADDING > vh) {
			y = vh - MENU_HEIGHT - PADDING;
		}

		// Clamp so it doesn’t hug the very top/left
		x = Math.max(PADDING, x);
		y = Math.max(PADDING, y);

		const isFavourite = Boolean(favouriteChannels[String(channel.id)]);
		const channels = getSubtreeChannelIds(channel.id);

		setContextMenu({ x, y, channel, section, channels, isFavourite });
	};

	useEffect(() => {
		if (!contextMenu) return;

		const handleMouseDown = (event: MouseEvent) => {
			if (menuRef.current?.contains(event.target as Node | null)) {
				return;
			}
			setContextMenu(null);
		};

		window.addEventListener("mousedown", handleMouseDown);

		return () => {
			window.removeEventListener("mousedown", handleMouseDown);
		};
	}, [contextMenu]);

	const cancelEditingChannelName = () => {
		setEditingChannel(null);
	};

	const saveChannelName = (name: string, channelId: number) => {
		setEditingChannel(null);

		queryClient.setQueryData(channelsQueryKey, (prev: Channel[] | undefined) => {
			const prevChannels = prev ?? [];
			return prevChannels.map(channel =>
				channel.id === channelId ? { ...channel, title: name } : channel
			);
		});

		// Update tab name for this channel
		updateTabNameForChannel(String(channelId), name);

		updateChannelNameMutation.mutate({
			workspaceId: currentWorkspace.id,
			channelId: channelId,
			newChannelName: name,
		});
	};

	function flushSaves() {
		if (!pendingSavesRef.current.size) return;

		const snapshot = new Map(pendingSavesRef.current.entries());
		pendingSavesRef.current.clear();

		const newState: Record<string, boolean> = { ...sidebarState };

		for (const [channelId, expanded] of snapshot) {
			newState[channelId] = expanded;
		}

		updateSidebarStateMutation.mutate({
			workspaceId: Number(currentWorkspace.id),
			sidebarState: newState
		});
	}

	function scheduleFlush() {
		if (flushTimerRef.current) window.clearTimeout(flushTimerRef.current);
		flushTimerRef.current = window.setTimeout(() => {
			flushTimerRef.current = null;
			flushSaves();
		}, DEBOUNCE_MS);
	}

	// FIX THE SAVING OF SIDEBAR STATE IN DATABASE
	function queueSave(id: string, expanded: boolean) {
		pendingSavesRef.current.set(id, expanded);
		scheduleFlush();
	}

	useEffect(() => {
		window.addEventListener("beforeunload", flushSaves);
		
		return () => {
			window.removeEventListener("beforeunload", flushSaves);
		};
	}, [currentWorkspace.id, sidebarStateLoading, sidebarState]);

	useDndMonitor({
		onDragStart: (event: DragStartEvent) => {
			const channel = event.active.data?.current?.channel as Channel | undefined;
			const section = event.active.data?.current?.section;
    		const level = (event.active.data?.current?.level as number | undefined) ?? 0;
			if (channel && section) {
				setDragMeta({ channel, section, level });
			} else {
				setDragMeta(null);
			}

			const ae = event.activatorEvent as MouseEvent;
			if ('clientY' in ae && typeof ae.clientY === 'number') {
				pointerStartYRef.current = ae.clientY;
			} else {
				pointerStartYRef.current = null;
			}
		},
		onDragMove: (event: DragMoveEvent) => {
			const { active, over } = event;
			if (!over) {
				setInsertionHint(null);
				return;
			}

			const from = active.data?.current?.channel as Channel | undefined;
			const to = over.data?.current?.channel as Channel | undefined;
			const fromSection = active.data?.current?.section;
			const toSection = over.data?.current?.section;
			const fromLevel = active.data?.current?.level;
			const toLevel = over.data?.current?.level;

			const invalidMove =
				!from ||
				!to ||
				from.id === to.id ||
				isDescendant(to, from, channels) ||
				from.visibility !== to.visibility ||
				fromSection !== toSection ||
				(fromSection === "favourites" && fromLevel > 0 && toLevel === 0) ||
				(fromSection === "favourites" && fromLevel === 0 && toLevel > 0);

			if (invalidMove) {
				setInsertionHint(null);
				return;
			}
			
			const overRect = over.rect;
			const startY = pointerStartYRef.current;

			if (startY && overRect) {
				const pointerY = startY + event.delta.y;
				const overTop = overRect.top;
				const overBottom = overRect.top + overRect.height;
				const overHeight = overRect.height;

				const edgePercent = overHeight * 0.15;

				let edge: 'before' | 'after' | undefined;

				if (pointerY <= overTop + edgePercent) {
					edge = 'before';
				} else if (pointerY >= overBottom - edgePercent) {
					edge = 'after';
				} else {
					edge = undefined;
				}

				if (fromSection === "favourites" && fromLevel === 0 && toLevel === 0) {
					if (isExpanded(`fav-root-${to.id}`)) {
						edge = 'before';
					}
				}

				setInsertionHint({
					overId: to.id,
					edge,
					section: toSection,
				});
			}
		},
		onDragEnd: (event: DragEndEvent) => {
			const { active, over } = event;
			if (!over) return;

			const from = active.data?.current?.channel as Channel | undefined;
			const to = over.data?.current?.channel as Channel | undefined;
			const fromSection = active.data?.current?.section;
			const toSection = over.data?.current?.section;
			const fromLevel = active.data?.current?.level;
			const toLevel = over.data?.current?.level;

			const invalidMove =
				!from ||
				!to ||
				from.id === to.id ||
				isDescendant(to, from, channels) ||
				from.visibility !== to.visibility ||
				fromSection !== toSection ||
				(fromSection === "favourites" && fromLevel === 0 && toLevel > 0);
	
			if (invalidMove) {
				setInsertionHint(null);
				setDragMeta(null);
				return;
			}

			const overRect = over.rect;
			const startY = pointerStartYRef.current;

			if (startY && overRect) {
				const pointerY = startY + event.delta.y;
				const overTop = overRect.top;
				const overBottom = overRect.top + overRect.height;
				const overHeight = overRect.height;

				const edgePercent = overHeight * 0.15;

				let edge: 'before' | 'after' | undefined;

				if (pointerY <= overTop + edgePercent) {
					edge = 'before';
				} else if (pointerY >= overBottom - edgePercent) {
					edge = 'after';
				} else {
					edge = undefined;
				}

				if (fromSection === "favourites" && fromLevel > 0 && toLevel === 0) {
					edge = undefined;
				}

				if (fromSection === "favourites" && fromLevel === 0 && toLevel === 0) {
					if (isExpanded(`fav-root-${to.id}`)) {
						edge = 'before';
					}
				}

				setDropResult({
					fromChannel: from, 
					toChannel: to, 
					edge, 
					section: toSection, 
					fromLevel,
					toLevel, 
				});
			}

			setDragMeta(null);
			setInsertionHint(null);
		},
		onDragCancel: () => {
			setInsertionHint(null);
			setDragMeta(null);
			pointerStartYRef.current = null;
			document.body.classList.remove('cursor-pointer');
		}
	})

	const isDescendant = (potentialDescendant: Channel, potentialAncestor: Channel, allChannels: Channel[]): boolean => {
		if (potentialDescendant.id === potentialAncestor.id) {
			return false;
		}
		
		let currentChannel: Channel | undefined = potentialDescendant;
		while (currentChannel && currentChannel.parentId) {
			if (currentChannel.parentId === potentialAncestor.id) {
				return true;
			}
			const parentId: number | null = currentChannel.parentId;
			currentChannel = allChannels.find(ch => ch.id === parentId);
			if (!currentChannel) {
				break; 
			}
		}
		return false;
	};

	useEffect(() => {
		// We only draw the “between” line for sibling drops (edge defined)
		if (!insertionHint || insertionHint.edge === undefined) {
		  setInsertionTop(null);
		  return;
		}
	  
		const container = containerRef.current;
		if (!container) {
		  setInsertionTop(null);
		  return;
		}
	  
		// Each row already has: data-row-id={channel.id}
		const rowEl = container.querySelector<HTMLElement>(
		  `[data-row-id="${insertionHint.overId}-${insertionHint.section}"]`
		);

		if (!rowEl) {
		  setInsertionTop(null);
		  return;
		}
	  
		// Get rects in viewport coordinates
		const rowRect = rowEl.getBoundingClientRect();
		const containerRect = container.getBoundingClientRect();
	  
		// Choose the boundary between siblings:
		// - drag over top 15% of B  -> insert BEFORE B -> y = B.top
		// - drag over bottom 15% of A -> insert AFTER A -> y = A.bottom
		const viewportY =
		  insertionHint.edge === "before" ? rowRect.top : rowRect.bottom;
	  
		// Convert to container-local coordinates and account for scroll
		const localTop = viewportY - containerRect.top + container.scrollTop;
	  
		setInsertionTop(localTop);
	}, [insertionHint]);
	  
	const toggleExpanded = (id: string) => {
		const currentValue = typeof sidebarState?.[id] === "boolean" ? sidebarState[id] : false;
		const newVal = !currentValue;

		const updatedSidebarState = {...sidebarState, [id]: newVal };

		queryClient.setQueryData(
			sidebarStateQueryKey,
			(prev) => ({
				favouriteChannels: prev?.favouriteChannels ?? {},
				privateChannelOrders: prev?.privateChannelOrders ?? {},
				sidebarState: updatedSidebarState,
			}),
		);

		queueSave(id, newVal);
	};

	const toggleMultipleExpanded = (ids: string[], expand: boolean) => {
		const updatedSidebarState = {...sidebarState };
	
		for (const id of ids) {
			const currentValue = sidebarState[id] ?? false;
	
			if (currentValue === expand) continue;		
	
			updatedSidebarState[id] = expand;
	
			queueSave(id, expand);
		}

		queryClient.setQueryData(
			sidebarStateQueryKey,
			(prev) => ({
				favouriteChannels: prev?.favouriteChannels ?? {},
				privateChannelOrders: prev?.privateChannelOrders ?? {},
				sidebarState: updatedSidebarState,
			}),
		);
	};

	const isExpanded = (id: string) => {
		const val = sidebarState?.[id];
	  
		if (typeof val === "boolean") return val;
	  
		const defaultToTrue =
		  id === "private" || id === "public" || id === "favourites";
	  
		return defaultToTrue ? true : false;
	};

	// Extract current channel ID from URL
	const getActiveChannelId = () => {
		const pathParts = pathname.split("/");
		const channelIdIndex =
			pathParts.findIndex((part) =>
				[
					"chats",
					"docs",
					"boards",
					"meets",
					"sheets",
					"storage",
					"passwords",
					"automations",
					"projects",
				].includes(part),
			) + 1;
		return channelIdIndex > 0 && channelIdIndex < pathParts.length
			? pathParts[channelIdIndex]
			: null;
	};

	const activeChannelId = getActiveChannelId();

	// Overview button functionality
	const isOnOverviewPage = () => {
		const pathParts = pathname.split("/");
		// For Home, check if we're at the root workspace path
		if (!activeSection || activeSection === "home") {
			return (
				pathParts.length === 2 &&
				pathParts[1] === currentWorkspace.id.toString()
			);
		}
		return pathParts.length === 3 && pathParts[2] === activeSection; // e.g., /1/docs
	};

	const navigateToOverview = () => {
		const overviewPath = activeSection
			? `/${currentWorkspace.id}/${activeSection}`
			: `/${currentWorkspace.id}`;
		openTemporaryTab(
			{
				channelId: `overview-${activeSection || "home"}`,
				workspaceId: currentWorkspace.id.toString(),
				name: activeSection ? `${sectionInfo.name} Overview` : "Home",
				type: "project",
				isPinned: false,
				path: overviewPath,
			},
			defaultPanelId,
		);
	};

	const handleOverviewDoubleClick = () => {
		const overviewPath = activeSection
			? `/${currentWorkspace.id}/${activeSection}`
			: `/${currentWorkspace.id}`;
		openPermanentTab(
			{
				channelId: `overview-${activeSection || "home"}`,
				workspaceId: currentWorkspace.id.toString(),
				name: activeSection ? `${sectionInfo.name} Overview` : "Home",
				type: "project",
				isPinned: false,
				path: overviewPath,
			},
			defaultPanelId,
		);
	};
	
	const getOrderValue = (order: number) => {
		return order === 0 ? Number.MAX_SAFE_INTEGER : order;
	}

	const buildFavouriteHierarchy = (
		channels: Channel[],
		favouriteIds: Set<string>,
	) : ChannelWithChildren[] => {
		const channelMap = new Map<number, ChannelWithChildren>();
		const rootChannels: ChannelWithChildren[] = [];

		for (const channel of channels) {
			channelMap.set(channel.id, { ...channel, children: [] });
		}

		for (const channel of channels) {
			const channelWithChildren = channelMap.get(channel.id)!;
			const parentId = channel.parentId;

			const parent = parentId != null ? channelMap.get(parentId) : undefined;
			const isExplicitFavourite = favouriteIds.has(String(channel.id));

			if (parent) {
				parent.children.push(channelWithChildren);
			}

			const parentIsMissingInSet = !parent;

			if (parentIsMissingInSet || isExplicitFavourite) {
				rootChannels.push(channelWithChildren);
			}

			for (const node of channelMap.values()) {
				node.children.sort((a, b) => {
					return getOrderValue(a.order) - getOrderValue(b.order);
				})
			}

			rootChannels.sort((a, b) => {
				return getOrderValue(favouriteChannels[String(a.id)]) - getOrderValue(favouriteChannels[String(b.id)]);
			})
		}

		return rootChannels;
	}

	// Helper function to build hierarchy from flat array
	const buildChannelHierarchy = (channels: Channel[]): ChannelWithChildren[] => {
		const channelMap = new Map<number, ChannelWithChildren>();
		const rootChannels: ChannelWithChildren[] = [];

		const sorted = 
			[...channels].sort((a, b) => {
				// Use user-specific order for private channels, global order for public
				let ao: number;
				let bo: number;
				
				if (a.visibility === 'private') {
					ao = getOrderValue(privateChannelOrders[String(a.id)]);
				} else {
					ao = getOrderValue(a.order);
				}
				
				if (b.visibility === 'private') {
					bo = getOrderValue(privateChannelOrders[String(b.id)]);
				} else {
					bo = getOrderValue(b.order);
				}
				
				return ao - bo;
			});

		// Initialize all channels with empty children arrays
		for (const channel of sorted) {
			channelMap.set(channel.id, { ...channel, children: [] });
		}

		// Build parent-child relationships
		for (const channel of sorted) {
			const channelWithChildren = channelMap.get(channel.id)!;

			const parentId = channel.parentId;

			if (parentId) {
				const parent = channelMap.get(parentId);
				if (parent) {
					parent.children.push(channelWithChildren);
				} else {
					// Parent not in filtered channels, treat as root
					rootChannels.push(channelWithChildren);
				}
			} else {
				rootChannels.push(channelWithChildren);
			}
		}

		// Sort children within each parent
		for (const channel of channelMap.values()) {
			channel.children.sort((a, b) => {
				// Use user-specific order for private channels, global order for public
				let ao: number;
				let bo: number;
				
				if (a.visibility === 'private') {
					ao = getOrderValue(privateChannelOrders[String(a.id)]);
				} else {
					ao = getOrderValue(a.order);
				}
				
				if (b.visibility === 'private') {
					bo = getOrderValue(privateChannelOrders[String(b.id)]);
				} else {
					bo = getOrderValue(b.order);
				}
				
				return ao - bo;
			});
		}

		return rootChannels;
	};

	// Get section display name and icon
	const getSectionInfo = (section: string) => {
		const sectionMap = {
			projects: { name: "Projects", icon: FolderKanban },
			chats: { name: "Chats", icon: MessagesSquare },
			docs: { name: "Documents", icon: FileText },
			boards: { name: "Boards", icon: LayoutGrid },
			sheets: { name: "Sheets", icon: FileSpreadsheet },
			meets: { name: "Meetings", icon: Video },
			storage: { name: "Storage", icon: HardDrive },
			passwords: { name: "Passwords", icon: Lock },
			automations: { name: "Automations", icon: Zap },
		};
		return (
			sectionMap[section as keyof typeof sectionMap] || {
				name: section,
				icon: Home,
			}
		);
	};

	// const filteredChannels = channels.filter((channel) => {
	// 	if (activeSection === "home" || !activeSection) return true;
	// 	if (activeSection === "projects") return channel.type === "project";
	// 	if (activeSection === "chats")
	// 		return (
	// 			channel.type === "message-group" ||
	// 			channel.type === "dm" ||
	// 			channel.type === "self-dm"
	// 		);
	// 	if (activeSection === "docs") return channel.type === "doc";
	// 	if (activeSection === "sheets") return channel.type === "sheet";
	// 	if (activeSection === "boards") return channel.type === "board";
	// 	if (activeSection === "meets") return channel.type === "meeting";
	// 	if (activeSection === "storage") return channel.type === "storage";
	// 	if (activeSection === "passwords") return channel.type === "password";
	// 	if (activeSection === "automations") return channel.type === "automation";
	// 	return false;
	// });

	const filteredChannels = [...channels]

	const publicChannels = filteredChannels.filter(
		(channel) => channel.visibility === "public",
	);
	const privateChannels = filteredChannels.filter(
		(channel) => channel.visibility === "private",
	);

	const favouriteChannelsFiltered = filteredChannels.filter((channel) => {
		if (channel.id in favouriteChannels) return true;

		let parentId = channel.parentId;

		while (parentId != null) {
			const parentChannel = filteredChannels.find((ch) => ch.id === parentId);
			if (!parentChannel) return false;

			if (parentId in favouriteChannels) return true;

			parentId = parentChannel.parentId;
		}

		return false
	});

	const favouriteIds = new Set(Object.keys(favouriteChannels ?? {}));
	const favouriteHierarchy = buildFavouriteHierarchy(favouriteChannelsFiltered, favouriteIds);
	const publicHierarchy = buildChannelHierarchy(publicChannels);
	const privateHierarchy = buildChannelHierarchy(privateChannels);

	const nodeById = useMemo(() => {
		const m = new Map<number, ChannelWithChildren>();
		const add = (n: ChannelWithChildren) => {
			m.set(n.id, n);
			n.children.forEach(add);
		};

		publicHierarchy.forEach(add);
  		privateHierarchy.forEach(add);
  		return m;
	}, [publicHierarchy, privateHierarchy]);

	const getSubtreeChannelIds = (rootId: number): Channel[] => {
		const root = nodeById.get(rootId);
		if (!root) {
			return [];
		}

		const ids: Channel[] = [];

		const walk = (node: ChannelWithChildren) => {
			ids.push(node);
			node.children.forEach(walk);
		}

		walk(root);
		return(ids);
	}

	const draggedAsWithChildren = useMemo<ChannelWithChildren | null>(() => {
		if (!dragMeta) return null;
		return nodeById.get(dragMeta.channel.id) ?? { ...dragMeta.channel, children: [] };
	}, [dragMeta, nodeById]);

	function decideMode(
		targetChannel: ChannelWithChildren,
		edge: 'before' | 'after' | undefined
	): 'sibling' | 'child' {
		const isParent = targetChannel.children && targetChannel.children.length > 0;
		const expanded = isExpanded(String(targetChannel.id));
	  
		if (edge === 'before') {
		  return 'sibling';
		} else if (edge === 'after') {
		  return isParent && expanded ? 'child' : 'sibling';
		}

		return 'child';
	}

	useEffect(() => {
		if (dropResult && (isAdmin || dropResult.section === "favourites" || dropResult.section === "private")) {
			let { fromChannel, toChannel, edge, section, fromLevel, toLevel } = dropResult;
			let insertAtEnd: boolean = false;

			if (edge === 'before') {
				const prev = nodeById.get(toChannel.id)

				if (prev) {
					if (fromChannel.id === prev.id) {
						setDropResult(null);
						return;
					}

					const prevIsParent = prev.children && prev.children.length > 0;
					const prevExpanded = isExpanded(String(prev.id));
					const isDraggedItemChildOfPrev = prev.children && prev.children.some(child => child.id === fromChannel.id);

					if (prevIsParent && prevExpanded && !isDraggedItemChildOfPrev) {
						toChannel = prev;
						edge = undefined;
						insertAtEnd = true;
					}
				}
			}

			const targetChannel = nodeById.get(toChannel.id);
			if (!targetChannel) return;

			const mode = decideMode(targetChannel, edge);

			const newParentId = mode === "child" ? targetChannel.id : targetChannel.parentId ?? null;

			let siblings;

			if (section === "favourites" && fromLevel === 0 && toLevel === 0) {
				siblings = channels.filter((ch) => 
					ch.id in favouriteChannels &&
					ch.parentId === newParentId &&
					ch.id !== fromChannel.id
				).sort((a, b) => 
					getOrderValue(favouriteChannels[String(a.id)]) - getOrderValue(favouriteChannels[String(b.id)])
				)

				let newGroup: Channel[] = [];

				if (mode === "child") {
					setDropResult(null);
					return;
				} else {
					const targetIndex = siblings.findIndex((ch) => ch.id === toChannel.id);

					let insertIndex = siblings.length;
					if (targetIndex >= 0) {
						insertIndex = edge === "before" ? targetIndex : targetIndex + 1;
					}

					const updatedFromChannel: Channel = { ...fromChannel, parentId: newParentId }

					newGroup = [
						...siblings.slice(0, insertIndex),
						updatedFromChannel,
						...siblings.slice(insertIndex),
					]

					const currentFavChannels = {...favouriteChannels};

					newGroup.forEach((ch, idx) => {
						currentFavChannels[ch.id] = idx + 1;
					})

					queryClient.setQueryData(
						sidebarStateQueryKey,
						(prev) => ({
							favouriteChannels: currentFavChannels,
							privateChannelOrders: prev?.privateChannelOrders ?? {},
							sidebarState: prev?.sidebarState ?? {},
						}),
					);

					updateFavouriteChannelMutation.mutate({
						workspaceId: Number(currentWorkspace.id),
						channelOrders: currentFavChannels
					})
				}
			} else {
				// Filter siblings by visibility and parent
				siblings = channels.filter((ch) => 
					ch.visibility === fromChannel.visibility &&
					ch.parentId === newParentId &&
					ch.id !== fromChannel.id
				).sort((a, b) => {
					// Use user-specific order for private channels, global order for public
					if (a.visibility === 'private') {
						const aOrder = getOrderValue(privateChannelOrders[String(a.id)]);
						const bOrder = getOrderValue(privateChannelOrders[String(b.id)]);
						return aOrder - bOrder;
					} else {
						return getOrderValue(a.order) - getOrderValue(b.order);
					}
				})

				let newGroup: Channel[] = [];

				if (mode === "child") {
					const updatedFromChannel: Channel = { ...fromChannel, parentId: newParentId };

					if (fromChannel.parentId === newParentId) {
						setDropResult(null);
						return;
					}

					if (insertAtEnd) {
						newGroup = [...siblings, updatedFromChannel];
					} else {
						newGroup = [updatedFromChannel, ...siblings]
					}
				} else {
					const targetIndex = siblings.findIndex((ch) => ch.id === toChannel.id);

					let insertIndex = siblings.length;
					if (targetIndex >= 0) {
						insertIndex = edge === "before" ? targetIndex : targetIndex + 1;
					}

					const updatedFromChannel: Channel = { ...fromChannel, parentId: newParentId }

					newGroup = [
						...siblings.slice(0, insertIndex),
						updatedFromChannel,
						...siblings.slice(insertIndex),
					]
				}
				
				// Handle private channels differently from public channels
				if (fromChannel.visibility === 'private') {
					// Private channels use user-specific ordering
					const currentPrivateOrders = {...privateChannelOrders};
					
					newGroup.forEach((ch, idx) => {
						currentPrivateOrders[String(ch.id)] = idx + 1;
					});

					// Update cache optimistically
					const nextCache = channels.map((ch) => {
						const updated = newGroup.find(g => g.id === ch.id);
						if (updated) {
							return {
								...ch,
								parentId: newParentId,
							};
						}
						return ch;
					});

					queryClient.setQueryData(channelsQueryKey, nextCache);
					queryClient.setQueryData(
						sidebarStateQueryKey,
						(prev) => ({
							sidebarState: prev?.sidebarState ?? {},
							favouriteChannels: prev?.favouriteChannels ?? {},
							privateChannelOrders: currentPrivateOrders,
						}),
					);

					// Update private channel orders
					updatePrivateChannelOrdersMutation.mutate({
						workspaceId: Number(currentWorkspace.id),
						channelOrders: currentPrivateOrders
					});

					// Update parentId if it changed (parentId is still stored in channels table)
					// Use reorderChannels with order undefined for private channels
					// Only update the dragged channel's parentId (siblings already have correct parentId)
					if (fromChannel.parentId !== newParentId) {
						reorderChannelsMutation.mutate({
							workspaceId: Number(currentWorkspace.id),
							updates: [{
								channelId: fromChannel.id,
								order: 0, // Not used for private channels, but required by type
								parentId: newParentId,
							}]
						});
					}
				} else {
					// Public channels use global ordering
					const updates = new Map<number, { order: number; parentId: number | null }>();

					newGroup.forEach((ch, idx) => {
						updates.set(ch.id, {
							order: idx + 1,
							parentId: newParentId,
						})
					})

					const nextCache = channels.map((ch) => {
						const upd = updates.get(ch.id);
						if (!upd) return ch;
						return {
							...ch,
							parentId: upd.parentId,
							order: upd.order,
						};
					});

					queryClient.setQueryData(channelsQueryKey, nextCache);

					const updatesArray = Array.from(updates.entries()).map(
						([channelId, { order, parentId }]) => ({
						channelId,
						order,
						parentId,
						}),
					);

					reorderChannelsMutation.mutate({
						workspaceId: Number(currentWorkspace.id),
						updates: updatesArray
					})
				}
			}

			setDropResult(null);
		}
	}, [dropResult, publicHierarchy, privateHierarchy]);

	const createTabData = (channel: Channel) => createTabDataFromChannel(currentWorkspace.id, channel);

	const handleChannelClick = (channel: Channel) => {
		openTemporaryTab(createTabData(channel), defaultPanelId);
	};

	const handleChannelDoubleClick = (channel: Channel) => {
		const tabData = createTabData(channel);
		const existingTab = getTabByPath(tabData.path);

		if (existingTab) {
			// Tab already exists
			if (temporaryTab?.tabId === existingTab.id) {
				// It's the temporary tab - confirm it (make permanent)
				confirmTab(existingTab.id);
			}
			// If it's already a permanent tab, do nothing (per spec: channel can only be in one panel)
			return;
		}

		// No existing tab, open as permanent
		openPermanentTab(tabData, defaultPanelId);
	};

	const handleChannelMiddleClick = (e: React.MouseEvent, channel: Channel) => {
		// Middle mouse button is button 1
		if (e.button === 1) {
			e.preventDefault();
			// Open channel as permanent tab in background on middle click
			openTabInBackground(createTabData(channel), defaultPanelId);
		}
	};

	const sectionInfo = activeSection
		? getSectionInfo(activeSection)
		: { name: "Workspace", icon: Home };

	const isAdmin = useMemo(() => {
		const role = currentWorkspace.role?.toLowerCase?.();
		return (role === "owner" || role === "admin");
	}, [currentWorkspace.role]);

	if (sidebarStateLoading) {
		return <WorkspaceSidebarLoading />;
	}	

	return (
		<div 
			ref={containerRef}
			className="flex h-full flex-col relative overflow-auto"
			data-sidebar-overlay
		>
			{/* Overview Button - Show for Home and other sections */}
			{/* <div className="border-border border-b">
				<Button
					variant="ghost"
					className={`flex w-full items-center justify-start gap-3 p-4 text-left ${
						isOnOverviewPage()
							? "border-primary/20 bg-primary/10 font-medium text-primary"
							: "hover:bg-accent hover:text-accent-foreground"
					}`}
					onClick={navigateToOverview}
					onDoubleClick={handleOverviewDoubleClick}
					onMouseDown={(e) => {
						// Middle mouse button opens overview as permanent tab in background
						if (e.button === 1) {
							e.preventDefault();
							const overviewPath = activeSection
								? `/${currentWorkspace.id}/${activeSection}`
								: `/${currentWorkspace.id}`;
							openTabInBackground({
								channelId: `overview-${activeSection || "home"}`,
								workspaceId: currentWorkspace.id.toString(),
								name: activeSection ? `${sectionInfo.name} Overview` : "Home",
								type: "project",
								isPinned: false,
								path: overviewPath,
							});
						}
					}}
				>
					<Home size={16} />
					<span className="font-medium">
						{activeSection ? `${sectionInfo.name} Overview` : "Home Overview"}
					</span>
				</Button>
			</div> */}

			{deleteChannelPopup && (
				<div
					className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70"
					onClick={() => setDeleteChannelPopup(null)}
				>
					<div
						className="w-full max-w-lg max-h-[80vh] overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-xl flex flex-col"
						onClick={(e) => e.stopPropagation()}
						role="dialog"
						aria-modal="true"
						aria-label="Delete Channel"
					>
						{/* Header */}
						<div className="flex items-start justify-between gap-4 p-6 pb-3 shrink-0">
							<div>
								<div className="text-xl font-bold">Delete Channel</div>
								<div className="mt-1 text-sm text-muted-foreground">
									Are you sure you want to delete this channel?
								</div>
							</div>

							<button
								type="button"
								className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
								onClick={() => setDeleteChannelPopup(null)}
								aria-label="Close"
							>
								<X className="h-5 w-5" />
							</button>
						</div>

						<div className="px-6 pb-4 mb-4">
							<div className="rounded-xl bg-muted/30 p-6 flex items-center justify-center text-center">
								<div className="text-base font-medium truncate max-w-full">
									{deleteChannelPopup.channel.title}
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="flex items-center gap-3 p-6 pt-0 shrink-0">
							<button
								type="button"
								className="h-10 rounded-xl px-6 bg-muted hover:bg-muted/80 flex-1"
								onClick={() => setDeleteChannelPopup(null)}
							>
								Cancel
							</button>

							<button
								type="button"
								className="h-10 rounded-xl px-6 text-white bg-red-500 hover:bg-red-600/90 flex-1"
								onClick={() => {
									queryClient.setQueryData(channelsQueryKey, (prev: Channel[] | undefined) => {
										if (!prev) return prev;
										
										return prev.filter((c) => c.id !== deleteChannelPopup.channel.id);
									});

									deleteChannelMutation.mutate({
										workspaceId: currentWorkspace.id,
										channelId: deleteChannelPopup.channel.id
									});

									setDeleteChannelPopup(null);
								}}
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Favourite Section */}
			{favouriteHierarchy.length > 0 &&
				<div className="border-border border-b">
					<div
						onClick={() => toggleExpanded("favourites")}
						onDoubleClick={(e) => e.preventDefault()}
						className="flex w-full items-center justify-between gap-2 bg-muted/30 px-4 py-2 transition-colors hover:bg-muted/50"
					>
						<div className="flex flex-1 items-center gap-2">
							{isExpanded("favourites") ? (
								<ChevronDown size={14} />
							) : (
								<ChevronRight size={14} />
							)}
							<div className="font-medium text-foreground text-sm">Favourites</div>
						</div>
					</div>
					{isExpanded("favourites") && (
						<div className="flex flex-col p-2 transition-all duration-200">
							{favouriteHierarchy.map((channel) => (
								<ChannelTreeItem
									key={`favourites-${channel.id}`}
									channel={channel}
									onChannelClick={handleChannelClick}
									onChannelDoubleClick={handleChannelDoubleClick}
									onChannelMiddleClick={handleChannelMiddleClick}
									activeChannelId={activeChannelId}
									workspaceId={currentWorkspace.id.toString()}
									section={"favourites"}
									isChannelExpanded={(id) => isExpanded(id)}
									toggleExpanded={(id) => toggleExpanded(id)}
									onContextMenu={handleChannelContextMenu}
									editingChannel={editingChannel}
									cancelEditingChannelName={cancelEditingChannelName}
									saveChannelName={saveChannelName}
								/>
							))}
						</div>
					)}
				</div>
			}

			{/* Public Section */}
			<div className="border-border border-b">
				<div
					onClick={() => toggleExpanded("public")}
					onDoubleClick={(e) => e.preventDefault()}
					className="flex w-full items-center justify-between gap-2 bg-muted/30 px-4 py-2 transition-colors hover:bg-muted/50"
				>
					<div className="flex flex-1 items-center gap-2">
						{isExpanded("public") ? (
							<ChevronDown size={14} />
						) : (
							<ChevronRight size={14} />
						)}
						<div className="font-medium text-foreground text-sm">Public</div>
					</div>
					{isAdmin &&
						<button
							onClick={(e) => {
								e.stopPropagation();
								setIsDialogOpen(true);
								setCreateChannelVisibility("public");
							}}
							className="text-muted-foreground transition-colors hover:text-foreground"
						>
							<Plus size={18} />
						</button>
					}
				</div>
				{isExpanded("public") && (
					publicHierarchy.length > 0 ? (
						<div className="flex flex-col p-2 transition-all duration-200">
							{publicHierarchy.map((channel) => (
								<ChannelTreeItem
									key={channel.id}
									channel={channel}
									onChannelClick={handleChannelClick}
									onChannelDoubleClick={handleChannelDoubleClick}
									onChannelMiddleClick={handleChannelMiddleClick}
									activeChannelId={activeChannelId}
									workspaceId={currentWorkspace.id.toString()}
									section={"public"}
									isChannelExpanded={isExpanded}
									toggleExpanded={toggleExpanded}
									onContextMenu={handleChannelContextMenu}
									editingChannel={editingChannel}
									cancelEditingChannelName={cancelEditingChannelName}
									saveChannelName={saveChannelName}
								/>
							))}
						</div>
					) : (
						<div className="px-4 py-3 text-xs text-muted-foreground">
							No public channels. Click <span className="font-medium">+</span> to add one.
						</div>
					)
				)}
			</div>

			{/* Private Section */}
			<div className="border-border border-b">
				<div
					onClick={() => toggleExpanded("private")}
					onDoubleClick={(e) => e.preventDefault()}
					className="flex w-full items-center justify-between gap-2 bg-muted/30 px-4 py-2 transition-colors hover:bg-muted/50"
				>
					<div className="flex flex-1 items-center gap-2">
						{isExpanded("private") ? (
							<ChevronDown size={14} />
						) : (
							<ChevronRight size={14} />
						)}
						<div className="font-medium text-foreground text-sm">Private</div>
					</div>
					<button
						onClick={(e) => {
							e.stopPropagation();
							setIsDialogOpen(true);
							setCreateChannelVisibility("private");
						}}
						className="text-muted-foreground transition-colors hover:text-foreground"
					>
						<Plus size={18} />
					</button>
				</div>
				{isExpanded("private") && (
					privateHierarchy.length > 0 ? (
						<div className="flex flex-col p-2 transition-all duration-200">
							{privateHierarchy.map((channel) => (
								<ChannelTreeItem
									key={channel.id}
									channel={channel}
									onChannelClick={handleChannelClick}
									onChannelDoubleClick={handleChannelDoubleClick}
									onChannelMiddleClick={handleChannelMiddleClick}
									activeChannelId={activeChannelId}
									workspaceId={currentWorkspace.id.toString()}
									section={"private"}
									isChannelExpanded={isExpanded}
									toggleExpanded={toggleExpanded}
									onContextMenu={handleChannelContextMenu}
									editingChannel={editingChannel}
									cancelEditingChannelName={cancelEditingChannelName}
									saveChannelName={saveChannelName}
								/>
							))}
						</div>
					) : (
						<div className="px-4 py-3 text-xs text-muted-foreground">
							No private channels. Click <span className="font-medium">+</span> to add one.
						</div>
					)
				)}
			</div>

			<CreateChannelDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} channelVisibility={createChannelVisibility}/>

			{contextMenu && (
				<div
					className="fixed z-50 w-70 rounded-md border bg-popover text-popover-foreground shadow-lg py-1"
					style={{ top: contextMenu.y, left: contextMenu.x }}
					ref={menuRef}
					onClick={(e) => e.stopPropagation()}
				>
					<button
						type="button"
						className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
						onClick={() => {
							if (contextMenu.isFavourite) {
								const channelToRemove = contextMenu.channel;

								const idsToCollapse = contextMenu.channels.map((ch) =>
									ch.id === channelToRemove.id
									 	? `fav-root-${ch.id}`  
									  	: `fav-${ch.id}`      
								);

								toggleMultipleExpanded(idsToCollapse, false);

								queryClient.setQueryData(sidebarStateQueryKey, (prev: any) => {
									const prevFav: FavouriteChannels = prev?.favouriteChannels ?? {};
								
									delete prevFav[channelToRemove.id];
								
									return {
									  sidebarState: prev?.sidebarState ?? {},
									  favouriteChannels: prevFav,
									  privateChannelOrders: prev?.privateChannelOrders ?? {},
									};
								});

								removeFavouriteChannelMutation.mutate({
									workspaceId: currentWorkspace.id,
									channelIds: [String(channelToRemove.id)]
								})

								setContextMenu(null);
							} else {
								const channelToAdd = contextMenu.channel;

								let channelOrdersToUpsert: FavouriteChannels = {};

								queryClient.setQueryData(sidebarStateQueryKey, (prev: any) => {
									const prevFav: FavouriteChannels = prev?.favouriteChannels ?? {};

									const existingValues = Object.values(prevFav);
									const maxOrder = existingValues.length
										? Math.max(...existingValues)
										: 0;

									const order = maxOrder + 1;

									channelOrdersToUpsert[channelToAdd.id] = order;
									prevFav[channelToAdd.id] = order;
								
									return {
									  sidebarState: prev?.sidebarState ?? {},
									  favouriteChannels: prevFav,
									  privateChannelOrders: prev?.privateChannelOrders ?? {},
									};
								});

								upsertFavouriteChannelMutation.mutate({
									workspaceId: currentWorkspace.id,
									channelOrders: channelOrdersToUpsert
								})
								setContextMenu(null);
							}
						}}
					>
						{contextMenu.isFavourite ? "Remove from favourite" : "Add to favourite"}
					</button>
					{(isAdmin || contextMenu.channel.visibility === "private") && (
						<>
							<button
								type="button"
								className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
								onClick={() => {
									// queryClient.setQueryData(channelsQueryKey, (prev: Channel[] | undefined) => {
									// 	const prevChannels = prev ?? [];
									// 	const updatedChannels = prevChannels.filter((ch) => ch.id !== contextMenu.channel.id)

									// 	return updatedChannels;
									// });

									setEditingChannel({
										channelID: contextMenu.channel.id,
										section: contextMenu.section
									});
									setContextMenu(null)
								}}
							>
								Rename
							</button>

							<button
								type="button"
								className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-red-500/10"
								onClick={() => {
									// queryClient.setQueryData(channelsQueryKey, (prev: Channel[] | undefined) => {
									// 	const prevChannels = prev ?? [];
									// 	const updatedChannels = prevChannels.filter((ch) => ch.id !== contextMenu.channel.id)

									// 	return updatedChannels;
									// });

									// deleteChannelMutation.mutate({
									// 	workspaceId: currentWorkspace.id,
									// 	channelId: contextMenu.channel.id
									// });

									setDeleteChannelPopup({ channel: contextMenu.channel });
									setContextMenu(null);
								}}
							>
								Delete
							</button>
						</>
					)}
				</div>
			)}

			{insertionHint?.edge !== undefined && insertionTop !== null && (isAdmin || insertionHint?.section === "favourites" || insertionHint?.section === "private") && (
				<div
					className="pointer-events-none absolute left-0 right-0"
					style={{
					top: insertionTop,
					height: 2,
					background: 'var(--primary)',
					opacity: 0.85,
					zIndex: 30,
					} as React.CSSProperties}
				/>
			)}

			{draggedAsWithChildren && dragMeta && (
				<DragOverlay dropAnimation={null}>
					<ChannelTreeItem
						channel={draggedAsWithChildren}
						onChannelClick={handleChannelClick}
						onChannelDoubleClick={handleChannelDoubleClick}
						onChannelMiddleClick={handleChannelMiddleClick}
						activeChannelId={activeChannelId}
						workspaceId={currentWorkspace.id.toString()}
						dragOverlay={true}
						section={dragMeta.section}
						level={dragMeta.level}
						isChannelExpanded={isExpanded}
						toggleExpanded={toggleExpanded}
						editingChannel={editingChannel}
						cancelEditingChannelName={cancelEditingChannelName}
						saveChannelName={saveChannelName}
					/>
				</DragOverlay>
			)}
		</div>
	);
};

export default WorkspaceSidebar;
